import { useLogout } from "@/common/hooks/use-logout";
import { ButtonColorVariant, ButtonSize, ButtonVariant, buttonVariants } from "@/components/ui/button";
import { Container } from "@/components/ui/container";
import i18next from "i18next";
import { RotateCcw } from "lucide-react";
import { Activity, ComponentType, SVGProps } from "react";
import { Link } from "wouter";
import CarCrashedSvg from "./_assets/car-crashed.svg?react";
import { CarRunner } from "./_components/car-runner";
import type { ErrorCode } from "./_helpers/error-code";

interface ErrorConfig {
  title: string;
  message: string;
  description: string;
}

const ERROR_DEFAULTS: Record<ErrorCode, ErrorConfig> = {
  400: {
    title: "400",
    message: i18next.t("common:InvalidRequest"),
    description: i18next.t("common:InvalidRequestDescription"),
  },
  401: {
    title: "401",
    message: i18next.t("common:LoginRequired"),
    description: i18next.t("common:LoginRequiredDescription"),
  },
  403: {
    title: "403",
    message: i18next.t("common:AccessDenied"),
    description: i18next.t("common:AccessDeniedDescription"),
  },
  404: {
    title: "404",
    message: i18next.t("common:PageNotFound"),
    description: i18next.t("common:PageNotFoundDescription"),
  },
  500: {
    title: "500",
    message: i18next.t("common:InternalServerError"),
    description: i18next.t("common:InternalServerErrorDescription"),
  },
  502: {
    title: "502",
    message: i18next.t("common:GatewayError"),
    description: i18next.t("common:GatewayErrorDescription"),
  },
  503: {
    title: "503",
    message: i18next.t("common:ServiceUnavailable"),
    description: i18next.t("common:ServiceUnavailableDescription"),
  },
};

const UNKNOWN_ERROR_DEFAULT: ErrorConfig = {
  title: i18next.t("common:Error"),
  message: i18next.t("common:UnknownError"),
  description: i18next.t("common:UnknownErrorDescription"),
};

interface ErrorPageProps {
  /** HTTP error code to display. Determines default title, message, and description; `null` shows an unknown error */
  errorCode?: ErrorCode | null;
  /** Custom title to override the default for the error code */
  title?: string;
  /** Custom message to override the default for the error code */
  message?: string;
  /** Custom description to override the default for the error code */
  description?: string;
  /** Route to navigate back to */
  backTo?: string;
  /** Label for the back button */
  backToLabel?: string;
  /** Custom SVG component to display instead of the default car crashed icon */
  customIcon?: ComponentType<SVGProps<SVGSVGElement>>;
  /** Hide action buttons (back, reload) */
  hideActions?: boolean;
  /** Hide only the back button */
  hideBackButton?: boolean;
  /** Hide only the reload button */
  hideReloadButton?: boolean;
  /** Route to fully reload instead of refreshing the current page */
  reloadTo?: string;
}

export function ErrorPage({
  errorCode = 404,
  title,
  message,
  description,
  backTo = "~/",
  backToLabel = i18next.t("fit:BackToOverview"),
  customIcon,
  hideActions = false,
  hideBackButton = false,
  hideReloadButton = false,
  reloadTo,
}: ErrorPageProps) {
  const logout = useLogout();
  const defaults = (errorCode === null ? undefined : ERROR_DEFAULTS[errorCode]) ?? UNKNOWN_ERROR_DEFAULT;
  const displayTitle = title ?? defaults.title;
  const displayMessage = message ?? defaults.message;
  const displayDescription = description ?? defaults.description;
  const IconComponent = customIcon ?? CarCrashedSvg;

  const handleReload = () => {
    if (reloadTo) {
      window.location.assign(reloadTo);
      return;
    }

    window.location.reload();
  };

  return (
    <div className="relative flex min-h-[calc(100vh-4rem)] items-center justify-center overflow-hidden p-4 sm:p-8">
      <div className="relative z-10 mx-auto max-w-4xl space-y-6 text-center sm:space-y-8">
        <CarRunner icon={IconComponent} />
        <Container className="space-y-4 sm:space-y-6">
          <div className="space-y-2 sm:space-y-3">
            <h1
              className={`text-primary p-2 font-bold ${displayTitle.length > 3 ? "text-3xl sm:text-4xl" : "text-5xl sm:text-7xl"}`}
            >
              {displayTitle}
            </h1>
            <h2 className="text-foreground/90 text-xl font-semibold sm:text-3xl">{displayMessage}</h2>
          </div>

          <p className="text-muted-foreground mx-auto max-w-sm text-base leading-relaxed sm:max-w-4xl sm:text-lg">
            {displayDescription}
          </p>

          <Activity mode={hideActions ? "hidden" : "visible"}>
            <div className="flex flex-col justify-center gap-3 pt-4 sm:flex-row">
              <Activity mode={hideBackButton ? "hidden" : "visible"}>
                <Link
                  className={buttonVariants({ size: ButtonSize.Lg })}
                  to={backTo}
                  aria-label={`Navigate to ${backToLabel}`}
                >
                  {backToLabel}
                </Link>
              </Activity>

              <Activity mode={hideReloadButton ? "hidden" : "visible"}>
                <button
                  type="button"
                  onClick={handleReload}
                  className={buttonVariants({
                    size: ButtonSize.Lg,
                    variant: ButtonVariant.Outlined,
                    colorVariant: ButtonColorVariant.Secondary,
                    className: "group",
                  })}
                  aria-label={i18next.t("common:ReloadPage")}
                >
                  <RotateCcw className="mr-2 h-4 w-4 transition-transform duration-500 group-hover:-rotate-180" />
                  <span className="hidden sm:inline">{i18next.t("common:ReloadPage")}</span>
                  <span className="sm:hidden">{i18next.t("common:Reload")}</span>
                </button>
              </Activity>

              <button
                type="button"
                onClick={logout}
                className={buttonVariants({ size: ButtonSize.Lg, variant: ButtonVariant.Filled })}
                aria-label={i18next.t("common:Logout")}
              >
                {i18next.t("common:Logout")}
              </button>
            </div>
          </Activity>
        </Container>
      </div>
    </div>
  );
}
